import React, { useEffect, useRef, useState } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableRow, Checkbox, Chip } from '@mui/material';
import { makeStyles } from '@material-ui/core';
import TimeTrackingService from '../../../services/TimeTrackingService';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    overflowX: 'auto',
    borderRadius: 12,
    border: '1px solid #e1e6ee',
    background: 'linear-gradient(180deg, #ffffff 0%, #f6f8fb 100%)',
    boxShadow: '0 8px 18px rgba(24, 39, 75, 0.08)',
  },
  tableContainer: {
    borderRadius: 12,
    border: '1px solid #e0e6ef',
    backgroundColor: '#ffffff',
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    minWidth: 1100,
  },
  tableViewport: {
    height: 600,
    width: '100%',
    overflow: 'auto',
    borderRadius: 10,
  },
  headerStyle: {
    borderRight: '1px solid #d9dee7',
    borderBottom: '1px solid #d9dee7',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#eef2f7',
    fontWeight: '700 !important',
    textAlign: 'center',
    color: '#243142',
    letterSpacing: 0.3,
  },
  cellStyle: {
    border: '1px solid #e2e6ee',
    fontSize: '13px!important',
    textAlign: 'center',
    color: '#2f3a4c',
    backgroundColor: '#ffffff',
    padding: '6px 8px',
  },
  bodyRow: {
    '&:nth-of-type(even)': {
      backgroundColor: '#fafbfc',
    },
    '&:hover': {
      backgroundColor: '#f0f6ff',
    },
  },
  stickyCell: {
    backgroundColor: '#ffffff',
  },
  tableFooter: {
    bottom: 0,
    width: '100%',
    backgroundColor: '#f3f5f9',
    borderTop: '2px solid #d6dbe4',
    position: 'sticky',
    height: 56,
    zIndex: 2,
    fontWeight: 700,
  },
  emptyState: {
    textAlign: 'center',
    height: 500,
    color: '#b42318',
    fontWeight: 600,
  },
  emptyStateChip: {
    textAlign: 'center',
    marginLeft: theme.spacing(54),
  },
}));

const TimeTrackingEtatFunding = (props) => {
  const {
    filters,
    filtersVersion,
    setMessage,
    setOpenLoadingSimpleBackdrop,
    handleSelectAllChange,
    selectAll,
    selectedUsers,
    selectedUsersForFunding,
    handleCheckboxChange,
    renderSkeletonRows,
    valueTab,
  } = props;
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [activeSkeletonLoading, setActiveSkeletonLoading] = useState(true);
  const [financements, setFinancements] = useState([]);
  const [loadedFundingVersion, setLoadedFundingVersion] = useState(-1);
  const fundingRequestVersionRef = useRef(null);
  const selectedUsersForFundingRef = useRef(selectedUsersForFunding);

  const fundingColumns = financements.length > 0 ? financements.length : 1;
  const totalColumns = 7 + fundingColumns + 2;
  const totalsByFunding = financements.reduce((acc, funding) => {
    acc[funding.id_financement_mis] = 0;
    return acc;
  }, {});

  rows.forEach((row) => {
    (row.missions_by_funding || []).forEach((mission) => {
      const fundingId = mission.id_financement_mis;
      if (fundingId === null || fundingId === undefined || totalsByFunding[fundingId] === undefined) {
        return;
      }
      totalsByFunding[fundingId] += Number(mission.mission_date_count || 0);
    });
  });

  const totalMissions = rows.reduce((sum, row) => sum + Number(row.mission_number || 0), 0);

  useEffect(() => {
    selectedUsersForFundingRef.current = selectedUsersForFunding;
  }, [selectedUsersForFunding]);

  useEffect(() => {
    if (valueTab !== 1 || loadedFundingVersion === filtersVersion || fundingRequestVersionRef.current === filtersVersion) {
      return;
    }

    const targetVersion = filtersVersion;
    fundingRequestVersionRef.current = targetVersion;
    let isMounted = true;

    const fetchData = async () => {
      setActiveSkeletonLoading(true);
      setRows([]);
      setFinancements([]);
      setOpenLoadingSimpleBackdrop(true);
      try {
        const response = await TimeTrackingService.getTimeTrackingFundingReport(filters, selectedUsersForFundingRef.current || []);

        if (!isMounted || fundingRequestVersionRef.current !== targetVersion) {
          return;
        }

        if (response && response.status && response.data) {
          const dataRows = response.data.timeTrackingStatus || [];
          setRows(
            dataRows.map((row, index) => ({
              ...row,
              id: index + 1,
            }))
          );
          setFinancements(response.data.financements || []);
          setLoadedFundingVersion(targetVersion);
        } else {
          if (response && response.message) {
            setMessage({
              open: true,
              color: 'red',
              messageInfo: response.message,
            });
          }
        }
      } catch (error) {
        if (!isMounted || fundingRequestVersionRef.current !== targetVersion) {
          return;
        }

        setMessage({
          open: true,
          color: 'red',
          messageInfo: 'Erreur lors de la recuperation des donnees.',
        });
      } finally {
        if (isMounted && fundingRequestVersionRef.current === targetVersion) {
          setActiveSkeletonLoading(false);
          setOpenLoadingSimpleBackdrop(false);
          fundingRequestVersionRef.current = null;
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [filters, filtersVersion, loadedFundingVersion, setMessage, setOpenLoadingSimpleBackdrop, valueTab]);

  useEffect(() => {
    if (valueTab === 1 && loadedFundingVersion === filtersVersion) {
      setActiveSkeletonLoading(false);
      setOpenLoadingSimpleBackdrop(false);
    }
  }, [filtersVersion, loadedFundingVersion, setOpenLoadingSimpleBackdrop, valueTab]);

  if (valueTab !== 1) {
    return null;
  }

  return (
    <Paper className={classes.paper} variant="outlined">
      <div className={classes.tableViewport}>
        <TableContainer className={classes.tableContainer} style={{ overflow: 'unset' }}>
          <Table className={classes.table} style={{ fontSize: 10 }} size="small" maxHeight={600}>
            <TableRow style={{ position: 'sticky', top: 0, zIndex: 2, fontSize: 8 }}>
              <TableCell rowSpan={2} className={classes.headerStyle} sx={{ left: 0, zIndex: 6, position: 'sticky', width: '8%' }}>
                <Checkbox
                  color="primary"
                  checked={selectAll}
                  onChange={(event) => handleSelectAllChange(event, rows)}
                />
              </TableCell>
              <TableCell rowSpan={2} className={classes.headerStyle} sx={{ width: '8%' }}>N°</TableCell>
              <TableCell rowSpan={2} className={classes.headerStyle} sx={{ width: '28%' }}>Direction</TableCell>
              <TableCell rowSpan={2} className={classes.headerStyle}>Immatricule</TableCell>
              <TableCell rowSpan={2} className={classes.headerStyle} style={{ left: 60, zIndex: 6, position: 'sticky', width: 200 }}>Noms et prénoms</TableCell>
              <TableCell rowSpan={2} className={classes.headerStyle}>Service</TableCell>
              <TableCell rowSpan={2} className={classes.headerStyle}>Fonction</TableCell>
              <TableCell colSpan={fundingColumns} className={classes.headerStyle} align="center">Nombre des missions validées</TableCell>
              <TableCell rowSpan={2} className={classes.headerStyle}>Total général</TableCell>
              <TableCell rowSpan={2} className={classes.headerStyle}>Observation</TableCell>
            </TableRow>

            <TableRow style={{ position: 'sticky', top: 35, zIndex: 2, fontSize: 8 }} maxHeight={100}>
              {financements.length > 0 ? (
                financements.map((funding, index) => (
                  <TableCell key={funding.id_financement_mis || index} className={classes.headerStyle}>
                    {funding.libelle || funding.code_financement_mis || '-'}
                  </TableCell>
                ))
              ) : (
                <TableCell className={classes.headerStyle}>Aucun financement</TableCell>
              )}
            </TableRow>
            <TableBody>
              {activeSkeletonLoading && renderSkeletonRows()}
              {!activeSkeletonLoading && (rows.length === 0 || financements.length === 0) && (
                <TableRow>
                  <TableCell colSpan={totalColumns} className={classes.emptyState}>
                    <Chip className={classes.emptyStateChip} color="error" label="Aucune donnÃ©e disponible" />
                  </TableCell>
                </TableRow>
              )}
              {!activeSkeletonLoading && financements.length > 0 && rows.map((row) => {
                const rowFundingMap = {};
                (row.missions_by_funding || []).forEach((mission) => {
                  if (mission.id_financement_mis !== null && mission.id_financement_mis !== undefined) {
                    rowFundingMap[mission.id_financement_mis] = Number(mission.mission_date_count || 0);
                  }
                });
                return (
                  <TableRow key={row.id} className={classes.bodyRow}>
                    <TableCell className={`${classes.cellStyle} ${classes.stickyCell}`} sx={{ left: 0, zIndex: 1, position: 'sticky', backgroundColor: '#ffffff', width: '8%' }}>
                      <Checkbox
                        checked={selectedUsers.some((m) => m === row.user_id) || selectAll}
                        color="primary"
                        onChange={() => handleCheckboxChange(row, rows)}
                        onClick={(event) => event.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell className={classes.cellStyle} sx={{ width: '8%' }}>{row.id}</TableCell>
                    <TableCell className={classes.cellStyle} sx={{ width: '28%' }}>{row.direction_code}</TableCell>
                    <TableCell className={classes.cellStyle}>{row.registration_number}</TableCell>
                    <TableCell className={`${classes.cellStyle} ${classes.stickyCell}`} style={{ left: 60, zIndex: 1, position: 'sticky', backgroundColor: '#ffffff', width: 200 }}>
                      {row.nom_prenoms}
                    </TableCell>
                    <TableCell className={classes.cellStyle}>{row.service_code}</TableCell>
                    <TableCell className={classes.cellStyle}>{row.function_name}</TableCell>
                    {financements.length > 0 ? (
                      financements.map((funding, index) => (
                        <TableCell key={funding.id_financement_mis || index} className={classes.cellStyle}>
                          {rowFundingMap[funding.id_financement_mis] || 0}
                        </TableCell>
                      ))
                    ) : (
                      <TableCell className={classes.cellStyle}>-</TableCell>
                    )}
                    <TableCell className={classes.cellStyle}>{row.mission_number || 0}</TableCell>
                    <TableCell className={classes.cellStyle} style={{ color: 'blue' }}>{row.observation || ''}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter className={classes.tableFooter}>
              <TableRow>
                <TableCell colSpan={7} className={classes.cellStyle} style={{ textAlign: 'right' }}><strong>Total</strong></TableCell>
                {financements.length > 0 ? (
                  financements.map((funding, index) => (
                    <TableCell key={funding.id_financement_mis || index} className={classes.cellStyle}>
                      <strong>{totalsByFunding[funding.id_financement_mis] || 0}</strong>
                    </TableCell>
                  ))
                ) : (
                  <TableCell className={classes.cellStyle}><strong>0</strong></TableCell>
                )}
                <TableCell className={classes.cellStyle}><strong>{totalMissions}</strong></TableCell>
                <TableCell className={classes.cellStyle}></TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
    </Paper>
  );
};

export default TimeTrackingEtatFunding;
